import { TfiAnnouncement, TfiClose } from "react-icons/tfi";
import moment from "moment";
import { cn } from "../../helpers/cn";
import useGetMaintanceAnnouncement from "./useGetMaintanceAnnouncement";
import useLocalStorage from "../../hooks/useLocalStorage";
import { localStorageVariables } from "../../constants";
import { isClient, isLoggedIn } from "../../services/accountService";

const activePeriodCheck = (startTime, endTime) => {
  const now = moment();

  const hasValidDates = Boolean(startTime && endTime);

  return hasValidDates && now.isBetween(moment(startTime), moment(endTime));
};

const getMessageAudience = (message = "") => {
  const lower = message.trim().toLowerCase();

  if (lower.startsWith("client:")) return "client";
  if (lower.startsWith("customer:")) return "customer";
  if (lower.startsWith("auth:")) return "auth";
  if (lower.startsWith("all:")) return "all";
  if (lower.startsWith("general:")) return "general";

  return "general";
};

const stripAudiencePrefix = (message = "") =>
  message.replace(/^(client|customer|auth|all|general):\s*/i, "");

const shouldDisplayMessage = (message = "") => {
  const audience = getMessageAudience(message);

  switch (audience) {
    case "client":
      return isClient();
    case "customer":
      return !isClient();
    case "auth":
      return isLoggedIn();

    case "general":
    case "all":
    default:
      return true;
  }
};

function parseTextToElements(text) {
  if (typeof text !== "string") {
    return "";
  }

  const cleanText = stripAudiencePrefix(text);

  // Supports:
  // <link to="http://google.com">Label</link>
  // <link>http://google.com</link>
  // **bold**, __underline__, _italic_, ~~strike~~
  const regex =
    /(<link\s+to=["'].*?["']>.*?<\/link>|<link>.*?<\/link>|\*\*.*?\*\*|__.*?__|_.*?_|~~.*?~~)/gi;

  const parts = cleanText.split(regex);

  return parts.map((part, i) => {
    // Links
    const lowerPart = part.toLowerCase();

    if (lowerPart.startsWith("<link to") && lowerPart.endsWith("</link>")) {
      const linkMatch = part.match(/<link\s+to="([^"]+)">(.*?)<\/link>/i);

      if (!linkMatch) {
        return <span key={i}>{part}</span>;
      }

      const [, url, label] = linkMatch;

      return (
        <a
          className="tw-text-white tw-underline tw-cursor-pointer tw-font-semibold hover:tw-text-gray-300"
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label || url}
        </a>
      );
    }

    if (lowerPart.startsWith("<link>") && lowerPart.endsWith("</link>")) {
      const url = part
        .replace(/<link>/i, "")
        .replace(/<\/link>/i, "")
        .trim();

      return (
        <a
          className="tw-text-white tw-underline tw-cursor-pointer tw-font-semibold hover:tw-text-gray-300"
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url}
        </a>
      );
    }
    // Bold
    if (part.startsWith("**") && part.endsWith("**")) {
      return <b key={i}>{part.slice(2, -2)}</b>;
    }

    // Underline
    if (part.startsWith("__") && part.endsWith("__")) {
      return <u key={i}>{part.slice(2, -2)}</u>;
    }

    // Italic
    if (part.startsWith("_") && part.endsWith("_")) {
      return <i key={i}>{part.slice(1, -1)}</i>;
    }

    // Strike
    if (part.startsWith("~~") && part.endsWith("~~")) {
      return <del key={i}>{part.slice(2, -2)}</del>;
    }

    return <span key={i}>{part}</span>;
  });
}

function Announcement() {
  const { data: response, isLoading, isError } = useGetMaintanceAnnouncement();
  const hasMessage = Boolean(response?.data?.message);
  const isWithinActiveTimeframe = activePeriodCheck(
    response?.data?.startTime,
    response?.data?.endTime,
  );

  const [lastClosedAnnouncementBanner, setLastClosedAnnouncementBanner] =
    useLocalStorage({
      key: localStorageVariables.lastClosedAnnouncementBanner,
      initialValue: null,
    });

  const currentAnnouncementBannerClosed =
    response?.data?.id === lastClosedAnnouncementBanner;

  const handleCloseAnnouncementBanner = () => {
    setLastClosedAnnouncementBanner(response?.data?.id);
  };

  const isAudienceMatch = shouldDisplayMessage(response?.data?.message);

  const isAnnouncmentActive =
    !isLoading &&
    !isError &&
    hasMessage &&
    isWithinActiveTimeframe &&
    !currentAnnouncementBannerClosed &&
    isAudienceMatch;

  return (
    <div
      className={cn(
        "tw-flex tw-items-center tw-bg-[#195483] tw-text-white tw-overflow-hidden tw-transition-all ",
        isAnnouncmentActive
          ? "tw:opacity-100 tw-px-4 tw-py-3 tw-border-b-2"
          : "tw-max-h-0 tw-opacity-0 tw-p-0",
      )}
      role="alert"
    >
      {isAnnouncmentActive && (
        <>
          <TfiAnnouncement
            className="tw-text-2xl tw-mr-5"
            style={{ minWidth: "1.4rem" }}
          />
          <div>{parseTextToElements(response?.data?.message)}</div>
          <TfiClose
            role="button"
            onClick={handleCloseAnnouncementBanner}
            className="tw-text-2xl tw-cursor-pointer no-copy tw-ml-auto"
            style={{ minWidth: "1.2rem" }}
          />
        </>
      )}
    </div>
  );
}

export default Announcement;
